import { DRILL_TYPES, DRILL_SCHEDULE } from './drillTypes.js'
import { monthlyReviewRow } from './ismCore.js'
import { monthNonConformities } from './horRules.js'

// OFFICER MONTHLY REVIEW - row-to-source mapping
//
// Each row's status is derived LIVE from the module that owns it. The officer
// does not fill dates - the system does. The officer reviews, notes exceptions,
// and signs ONCE for the whole month.
//
// source types:
//   'supabase' - table + how to detect activity in the review month
//   'manual'   - no module owns it yet; officer confirms + notes by hand
//   'ism'      - the ISM Audits module (audits held that month + findings open at month end; done when none is overdue)
//   'hor'      - Hours of Rest: done when every crew member on board has a sheet for the month; non-conformities listed
//   'orb'      - Oil Record Book: entries that month; done when every entry is signed and countersigned by the Master
//   'drill'    - one drill type of DRILL_TYPES (drillTypes.js): done when held that month, "Not due" when the SMS-16
//                schedule has no slot that month and it is not overdue (matched on the exact drill label)
//
// autoStatus() below is the ONE place that turns database rows into row statuses: the Monthly Review page and the
// AI (officer_review) both call it, with rows read by reviewSources().
//
// A row is AUTO-GREEN when its module has a record dated within the review month.
// dateField -> a YYYY-MM-DD (or ISO) column, matched by prefix.
// monthField -> a 'YYYY-MM' column, matched exactly.
// drillMatch -> regex on drill_type within drill_records.

export const SECTIONS = [
  {
    group: 'ISM',
    rows: [
      { key: 'crew_documents',   label: 'Crew Documents',               src: 'supabase', table: 'crew_docs',           dateField: 'created_at' },  // a crew certificate added that month
      { key: 'drill_reports',    label: 'Drill Reports',                src: 'supabase', table: 'drill_records',       dateField: 'drill_date' },
      { key: 'safety_equip',     label: 'Safety Equipment Checks',      src: 'supabase', table: 'lsa_checks',          monthField: 'period' },
      { key: 'engineering_rep',  label: 'Engineering Reports',          src: 'supabase', table: 'engine_log',          dateField: 'date' },
      { key: 'ism_checklists',   label: 'ISM Check Lists',              src: 'supabase', table: 'checklist_instances', dateField: 'completed_at' },
      { key: 'accident_rep',     label: 'Accidents / Incident Reports', src: 'manual' },
      { key: 'ism_audits',       label: 'ISM Audits / Non-conformities', src: 'ism' },      // ISM Audits module: done when no finding is overdue (monthlyReviewRow in ismCore.js)
      { key: 'passage_plans',    label: 'Passage Plans',                src: 'supabase', table: 'passage_plans',       dateField: 'departure_date' },
      { key: 'hours_rest',       label: 'Hours of Rest',                src: 'hor' },
      { key: 'orb',              label: 'Oil Record Book',              src: 'orb' },
    ],
  },
  {
    group: 'Ship Operations',
    rows: [
      { key: 'watch_schedule',    label: 'Watch Schedule',             src: 'supabase', table: 'watch_schedules', dateField: 'date_from' },
      { key: 'inventory',         label: 'Inventory',                  src: 'supabase', table: 'inventory',       dateField: 'created_at' },  // no updated column: new items added that month
      { key: 'supplies_order',    label: 'Supplies Order',             src: 'manual' },
      { key: 'itinerary_meeting', label: 'Itinerary Meeting',          src: 'manual' },
      { key: 'running_log',       label: 'Running Log Book',           src: 'supabase', table: 'running_log',     dateField: 'date' },
      { key: 'gmdss_log',         label: 'GMDSS Log',                  src: 'manual' },
      { key: 'garbage_log',       label: 'Garbage Log',                src: 'manual' },
      { key: 'official_log',      label: 'Official Log',               src: 'manual' },
      { key: 'chart_corrections', label: 'Chart Corrections and Supplies', src: 'manual' },
    ],
  },
  {
    group: 'Tender / Toy Operations',
    rows: [
      { key: 'tender_checks', label: 'Tender Equipment / Service and Documents Checks',   src: 'manual' },
      { key: 'rescue_tender', label: 'Rescue Tender Equipment / Service and Documents',   src: 'manual' },
      { key: 'seadoo_checks', label: 'Sea Doo Equipment / Service and Documents Checks',  src: 'manual' },
      { key: 'seabob_checks', label: 'Sea Bob Equipment / Service and Documents Checks',  src: 'manual' },
      { key: 'dive_checks',   label: 'Dive Equipment Checks / Fuel',                      src: 'manual' },
    ],
  },
  {
    group: 'Safety Checks',
    rows: [
      { key: 'ffe_checks',      label: 'Fire Fighting Equipment Checks',     src: 'supabase', table: 'lsa_checks', monthField: 'period' },
      { key: 'lsa_checks_row',  label: 'Life Saving Equipment Checks',       src: 'supabase', table: 'lsa_checks', monthField: 'period' },
      { key: 'alarms_shutdown', label: 'Alarms and Shutdown System Checks',  src: 'manual' },
      { key: 'grab_bag',        label: 'Grab Bag Checks',                    src: 'manual' },
      { key: 'medical_supply',  label: 'Ships Medical Supply Checks',        src: 'manual' },
    ],
  },
  {
    group: 'Drill Compliance',
    rows: [
      // keys kept from the first version (saved reviews use them); the rest are added for every other SMS-16 drill
      { key: 'drill_fire',     label: 'FIRE',                src: 'drill', drill: 'fire' },
      { key: 'drill_abandon',  label: 'ABANDON SHIP',        src: 'drill', drill: 'abandon_ship' },
      { key: 'drill_mob',      label: 'MOB',                 src: 'drill', drill: 'mob' },
      { key: 'drill_lost_tow', label: 'LOST TOW',            src: 'drill', drill: 'emergency_tow' },
      { key: 'drill_sopep',    label: 'SOPEP',               src: 'drill', drill: 'pollution' },
      { key: 'drill_steering', label: 'Emergency Steering',  src: 'drill', drill: 'emergency_steer' },
      { key: 'drill_medical',  label: 'Emergency Medical',   src: 'drill', drill: 'medical' },
      { key: 'drill_security', label: 'SECURITY',            src: 'drill', drill: 'security' },
      { key: 'drill_enclosed_space', label: 'Enclosed Space Rescue', src: 'drill', drill: 'enclosed_space' },
      { key: 'drill_collision',      label: 'Collision',             src: 'drill', drill: 'collision' },
      { key: 'drill_grounding',      label: 'Grounding / Foundering', src: 'drill', drill: 'grounding' },
      { key: 'drill_flooding',       label: 'Flooding / Hull Failure', src: 'drill', drill: 'flooding' },
      { key: 'drill_machinery_fail', label: 'Critical Machinery Failure', src: 'drill', drill: 'machinery_fail' },
      { key: 'drill_salvage',        label: 'Salvage of Own Ship',   src: 'drill', drill: 'salvage' },
      { key: 'drill_power_fail',     label: 'Power Failure (Dead Ship)', src: 'drill', drill: 'power_fail' },
    ],
  },
  {
    group: 'Crew Training and Certification',
    rows: [
      { key: 'safe_manning',          label: 'Safe Manning Compliance',                 src: 'manual' },
      { key: 'stcw',                  label: 'STCW Compliance',                         src: 'manual' },
      { key: 'engi_ml5',              label: 'ENG1 / ML5 Compliance',                   src: 'manual' },
      { key: 'deck_officer_training', label: 'Monthly Deck Officer Training / Testing', src: 'manual' },
      { key: 'cabin_checks',          label: 'Weekly Cabin Checks',                     src: 'manual' },
    ],
  },
]

export const ALL_ROWS = SECTIONS.flatMap(s => s.rows)

// ---------------------------------------------------------------------------
// reading + judging (shared by the page and the AI)
// ---------------------------------------------------------------------------
const pad2 = n => String(n).padStart(2, '0')
export const nextMonth = m => { const [y, mo] = m.split('-').map(Number); return mo === 12 ? (y + 1) + '-01' : y + '-' + pad2(mo + 1) }
export const prevMonth = m => { const [y, mo] = m.split('-').map(Number); return mo === 1 ? (y - 1) + '-12' : y + '-' + pad2(mo - 1) }
const monthOf = v => String(v || '').slice(0, 7)
const day = v => String(v || '').slice(0, 10)
const norm = v => String(v || '').trim().toLowerCase()
const DRILL_BY_KEY = Object.fromEntries(DRILL_TYPES.map(d => [d.key, d]))

// What to read for one month: [{ table, select, range?: {field, from, to}, eq?: {field, value}, noClient?, order?, limit }]
// range = from <= field < to (month bounds, also right for text YYYY-MM-DD columns). Drills read their history (overdue).
export function reviewSources(month) {
  const from = month + '-01', to = nextMonth(month) + '-01'
  const out = [], seen = new Set()
  const add = x => { const k = x.table + JSON.stringify(x.range || x.eq || ''); if (!seen.has(k)) { seen.add(k); out.push({ limit: 2000, ...x }) } }
  for (const r of ALL_ROWS.filter(r => r.src === 'supabase')) {
    if (r.table === 'drill_records') continue
    add(r.monthField ? { table: r.table, select: '*', eq: { field: r.monthField, value: month }, noClient: r.table === 'lsa_checks' }
      : { table: r.table, select: '*', range: { field: r.dateField, from, to } })
  }
  add({ table: 'drill_records', select: 'id,drill_type,drill_date,crew_present,created_at', range: { field: 'drill_date', from: '2000-01-01', to }, order: 'drill_date.desc' })
  add({ table: 'hours_rest', select: 'id,crew_member,crew_id,month,days,onboard', range: { field: 'month', from: prevMonth(month), to: nextMonth(month) + '-~' } })
  add({ table: 'crew', select: 'id,first_name,last_name,name,onboard,archived', limit: 500 })
  add({ table: 'oil_record_book', select: 'id,date,code,items,signed_by,master_signed_by', range: { field: 'date', from, to } })
  add({ table: 'lsa_items', select: 'id,name,type,location', limit: 1000 })
  add({ table: 'checklists', select: 'id,title', noClient: true, limit: 2000 })
  add({ table: 'ism_audits', select: 'audit_no,audit_type,status,audit_date', limit: 500 })
  add({ table: 'ism_findings', select: 'ref,grade,element,description,status,due_date,raised_date,closed_date,owner', limit: 2000 })
  return out
}

// readable line for one matched record
export function itemLabel(row, table, look = {}) {
  if (table === 'drill_records') return (row.drill_type || 'Drill') + (row.drill_date ? '  ·  ' + day(row.drill_date) : '')
  if (table === 'checklist_instances') { const t = (look.checklists || {})[row.checklist_id] || 'Checklist'; return t + (row.run_label ? ' (' + row.run_label + ')' : '') + (row.completed_at ? '  ·  ' + day(row.completed_at) : '') }
  if (table === 'passage_plans') return (row.title || (row.departure_port ? row.departure_port + ' → ' + (row.arrival_port || '') : 'Passage')) + (row.departure_date ? '  ·  ' + day(row.departure_date) : '')
  if (table === 'watch_schedules') return (row.name || 'Schedule') + (row.date_from ? '  ·  ' + day(row.date_from) + ' → ' + day(row.date_to) : '')
  if (table === 'lsa_checks') return ((look.lsa || {})[row.item_id] || 'Item') + (row.status ? '  ·  ' + String(row.status).toUpperCase() : '')
  if (table === 'crew_docs') return (row.title || 'Certificate') + '  ·  ' + day(row.created_at)
  if (table === 'inventory') return (row.part_name || 'Item') + ' (new)  ·  ' + day(row.created_at)
  if (table === 'engine_log' || table === 'running_log') return (row.location || row.voyage_origin || row.entry || 'Entry') + '  ·  ' + day(row.date)
  return JSON.stringify(row).slice(0, 60)
}

// rowsByTable: { table: [rows] } as read with reviewSources(month). today: ship's date YYYY-MM-DD.
// -> { [row key]: { done, date, count, items, label? } }  (label: text shown instead of "Done", e.g. "Not due")
export function autoStatus(rowsByTable, month, today) {
  const T = t => (rowsByTable && rowsByTable[t]) || []
  const look = {
    lsa: Object.fromEntries(T('lsa_items').map(i => [i.id, i.name || i.type || i.location || i.id])),
    checklists: Object.fromEntries(T('checklists').map(c => [c.id, c.title || 'Checklist'])),
  }
  const out = {}
  for (const r of ALL_ROWS) {
    if (r.src === 'supabase') {
      const rows = T(r.table).filter(row => r.monthField ? String(row[r.monthField] || '') === month : monthOf(row[r.dateField]) === month)
      if (!rows.length) continue
      let shown = rows
      if (r.table === 'drill_records') {       // latest run per drill type (re-runs do not stack)
        const by = {}
        for (const m of rows) { const k = String(m.drill_type || 'Drill'), d = String(m.drill_date || m.created_at || ''); if (!by[k] || d > String(by[k].drill_date || by[k].created_at || '')) by[k] = m }
        shown = Object.values(by)
      }
      const dates = rows.map(m => r.monthField ? month + '-01' : day(m[r.dateField])).filter(Boolean).sort()
      out[r.key] = { done: true, date: dates[dates.length - 1] || month + '-01', count: shown.length, items: shown.map(m => itemLabel(m, r.table, look)) }
    } else if (r.src === 'drill') {
      const d = DRILL_BY_KEY[r.drill]; if (!d) continue
      const recs = T('drill_records').filter(x => norm(x.drill_type) === norm(d.label) && x.drill_date && day(x.drill_date) < nextMonth(month) + '-01')
      const inMonth = recs.filter(x => monthOf(x.drill_date) === month).sort((a, b) => day(b.drill_date).localeCompare(day(a.drill_date)))
      if (inMonth.length) { out[r.key] = { done: true, date: day(inMonth[0].drill_date), count: inMonth.length, items: inMonth.map(x => itemLabel(x, 'drill_records')) }; continue }
      const last = recs.map(x => day(x.drill_date)).sort().pop()
      const [y, mo] = month.split('-').map(Number)
      const since = last ? (y * 12 + mo) - (Number(last.slice(0, 4)) * 12 + Number(last.slice(5, 7))) : null
      if (d.freqMonths && since != null && since > d.freqMonths) { out[r.key] = { done: false, date: null, count: 1, items: ['Overdue: last held ' + last + ' (every ' + d.freqMonths + ' month' + (d.freqMonths > 1 ? 's' : '') + ')'] }; continue }
      const slot = (DRILL_SCHEDULE[d.key] || []).includes(mo - 1)
      if (!slot) out[r.key] = { done: true, date: null, count: 0, items: [], label: d.freqMonths ? 'Not due' : 'As required' }
    } else if (r.src === 'hor') {
      const sheets = T('hours_rest').filter(x => String(x.month) === month)
      if (!sheets.length && !T('hours_rest').length) continue
      const onboard = T('crew').filter(c => c.onboard && !c.archived)
      const nm = c => String(c.name || [c.first_name, c.last_name].filter(Boolean).join(' ')).trim()
      const has = c => sheets.some(x => (x.crew_id && String(x.crew_id) === String(c.id)) || norm(x.crew_member) === norm(nm(c)) || norm(x.crew_member) === norm([c.first_name, c.last_name].filter(Boolean).join(' ')))
      const missing = onboard.filter(c => !has(c)).map(nm)
      const nc = monthNonConformities(T('hours_rest'), month)
      const items = [...missing.map(n => 'No sheet: ' + n), ...nc.items.map(i => i.crew_member + ' · ' + i.date + ': ' + i.rules.join(', '))]
      const done = sheets.length > 0 && missing.length === 0
      out[r.key] = { done, date: done ? month + '-01' : null, count: items.length, items, ...(done && nc.days ? { label: 'Done · ' + nc.days + ' non-conformit' + (nc.days === 1 ? 'y' : 'ies') } : {}), nc }
    } else if (r.src === 'orb') {
      const rows = T('oil_record_book').filter(x => monthOf(x.date) === month).sort((a, b) => day(a.date).localeCompare(day(b.date)))
      if (!rows.length) continue
      const unsigned = rows.filter(x => !x.signed_by), unmastered = rows.filter(x => x.signed_by && !x.master_signed_by)
      const items = rows.map(x => 'ORB ' + x.code + (x.items ? ' ' + x.items : '') + '  ·  ' + day(x.date) + (!x.signed_by ? '  ·  NOT SIGNED' : !x.master_signed_by ? '  ·  not countersigned' : ''))
      const done = !unsigned.length && !unmastered.length
      out[r.key] = { done, date: done ? day(rows[rows.length - 1].date) : null, count: rows.length, items, unsigned: unsigned.length, unmastered: unmastered.length }
    } else if (r.src === 'ism') {
      const au = T('ism_audits'), fi = T('ism_findings')
      if (!au.length && !fi.length) continue       // module not in use on this vessel
      const x = monthlyReviewRow(au, fi, month, today)
      if (x.done || x.items.length) out[r.key] = { done: x.done, date: x.done ? x.date : null, count: x.items.length, items: x.items }
    }
  }
  return out
}
